import React, { useEffect } from "react"
import { createBillingPortalSession } from "api/accounts"
import {
  Subscription, Plan as DioboxPlan, SubscriptionStatuses, ModalName,
} from "sharedTypes"
import {
  Box, Text, Heading, UnorderedList, ListItem, Button,
} from "@chakra-ui/react"
import { asDateWithTZ, asMoney } from "utilities/strings"
import ChangeBillingInterval from "pages/Account/Plan/ChangeBillingInterval"
import SubscriptionDowngrader from "pages/Account/Plan/Downgrader"
import UpgradeEnticer from "pages/Account/Plan/UpgradeEnticer"
import usePlans from "queries/plans"
import Spinner from "components/elements/Spinner"
import Link from "components/elements/Link"
import { useIntercom } from "react-use-intercom"
import PastDueAlert from "components/elements/PastDueAlert"
import { useQueryClient } from "react-query"
import { refreshEvents } from "context/actions"
import useModal from "services/useModal"
import useSearchParams from "services/useSearchParams"
import { useHistory } from "react-router-dom"

type Props = {
  subscription: Subscription | null
  showBilling: boolean
  onClickResolve: () => void
}

const Plan = ({ subscription, showBilling, onClickResolve }: Props) => {
  const { show: showIntercom } = useIntercom()
  const queryClient = useQueryClient()
  const showModal = useModal()
  const history = useHistory()
  const { session_id: sessionId } = useSearchParams<{ session_id?: string }>()
  const accountIsPastDue = subscription?.status === SubscriptionStatuses.PastDue

  // Handle Stripe Checkout success redirect
  useEffect(() => {
    if (sessionId) {
      const returnPath = sessionStorage.getItem("checkoutReturnPath") || "/account/plan"
      sessionStorage.removeItem("checkoutReturnPath")
      queryClient.invalidateQueries(["currentAccount"])
      refreshEvents(queryClient)
      showModal(ModalName.UpgradeSuccessful)
      history.replace(returnPath)
    }
  }, [queryClient, showModal, sessionId, history])

  const { isLoading, plans } = usePlans()
  if (isLoading) { return <Spinner /> }

  const currentPlan = (): DioboxPlan => {
    // If there's no plan, we'll select the Free plan
    if (!subscription) {
      return plans[0]
    }

    const plan = plans.find((p) => p.type === subscription.type)

    // If the plan is a legacy plan (like Standard or Basic) we should select Prof.
    if (!plan) {
      return plans[1]
    }

    return plan
  }

  const {
    type, licenses, amount, interval, cancelAtPeriodEnd, periodEndsAt, legacy,
  } = Object(subscription)

  const planInfo = () => {
    if (subscription) {
      return (
        <Box mb={4}>
          <Text mb={4} fontSize="2xl">
            {type}
            {" "}
            Subscription Plan
            { legacy && " (Legacy)" }
          </Text>
          <Text>
            Team Size:{" "}
            {licenses}
          </Text>
          <Text>
            {asMoney(amount / 100)}
            {interval ? `/${interval}` : " custom subscription"}
          </Text>
          {interval === "year" && <Text>Includes {legacy ? "10" : "40"}% discount</Text>}
          {!subscription.stripeId && (
          <Box mt={8} mb={12}>
            <Text mb={3}>Expires: {asDateWithTZ(subscription.periodEndsAt, "UTC", "PPP")}</Text>
            <Text>
              To extend your subscription, <Link onClick={showIntercom}>send us a message</Link>.
            </Text>
          </Box>
          )}
        </Box>
      )
    }

    return <Text fontSize="2xl">Free Plan</Text>
  }

  const openBillingPortal = () => createBillingPortalSession()
    .then((response) => {
      window.open(response.data.url, "_blank")
    })

  return (
    <Box px={32} py={8} maxW={1050} mx="auto">
      <Heading fontSize="4xl" mb={8} fontWeight="semibold">Plan</Heading>
      <Box>
        {planInfo()}
        {accountIsPastDue && <PastDueAlert onClickResolve={onClickResolve} />}
        {!accountIsPastDue && subscription?.stripeId && !cancelAtPeriodEnd && (
        <SubscriptionDowngrader subscription={subscription} />
        )}
        {subscription?.stripeId
        && subscription?.interval === "year"
        && !subscription?.legacy
        && !cancelAtPeriodEnd && <ChangeBillingInterval currentPlan={currentPlan()} toInterval="month" />}
        {subscription?.cancelAtPeriodEnd && (
        <Box mb={4}>
          <Text mb={1} color="red.600" fontWeight="bold">YOUR SUBSCRIPTION IS PENDING CANCELATION.</Text>
          <Text mb={1} color="red.600" fontWeight="bold">
            You will lose your account and your data on {asDateWithTZ(periodEndsAt, "UTC", "PPP")}. To keep your subscription, <Link onClick={showIntercom}>send us a message</Link>.
          </Text>
        </Box>
        )}
      </Box>
      {(!subscription || subscription?.stripeId) && !subscription?.cancelAtPeriodEnd
      && (
      <Box py={8}>
        <UpgradeEnticer subscription={subscription} currentPlan={currentPlan()} />
      </Box>
      )}
      {showBilling && (
      <>
        <Heading fontSize="4xl" mb={8} fontWeight="semibold">Billing</Heading>
        <Box
          p={8}
          bgColor="gray.50"
          borderWidth={1}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={4}
            mb={6}
          >
            <Text flex="1" fontSize="xl">
              Diobox uses Stripe for secure subscription and payment management.
            </Text>
            <Button colorScheme="blue" onClick={openBillingPortal} flexShrink={0} px={12}>
              Open Billing Portal
            </Button>
          </Box>
          <Text mb={2} fontSize="xl">
            In the Billing Portal you can:
          </Text>
          <UnorderedList spacing={0} listStylePos="inside" lineHeight="short">
            <ListItem>Update payment method</ListItem>
            <ListItem>Download invoices</ListItem>
            <ListItem>View upcoming charges</ListItem>
            <ListItem>Edit billing details</ListItem>
            <ListItem>Add a Tax or VAT ID</ListItem>
          </UnorderedList>
        </Box>
      </>
      )}
    </Box>
  )
}

export default Plan
